import { Add } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PanelSegment from '../../components/PanelSegment';
import PageTitle from '../../components/PageTitle';
import useViewportMode from '../../hooks/useViewportMode';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import { ProducerClient, ProducerCreateClientCommand } from '../../models/producer';

const ProducerClientCompaniesPage = () => {
    const { mode, isDesktop } = useViewportMode();
    const { accessInfo, hasRole } = useAccessInfo();
    const canManageClients = hasRole(Securables.producer_admin, true) || hasRole(Securables.producer, true);
    const isAdmin = hasRole(Securables.producer_admin, true);
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [clients, setClients] = useState<ProducerClient[]>([]);
    const [form, setForm] = useState({ name: '', firstName: '', lastName: '', email: '' });
    const load = async () => {
        if (!accessInfo.selectedProducerCompanyId)
            return;

        try {
            setBusy(true);
            const list = await new ProducerPortalClient().GetClients(accessInfo.selectedProducerCompanyId);
            setClients(list);
        } catch (error) {
            setError(error, 'Failed to load assigned clients');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessInfo.selectedProducerCompanyId]);

    const createClient = async () => {
        if (!accessInfo.selectedProducerCompanyId || !form.name || !form.firstName || !form.lastName || !form.email)
            return;

        try {
            setBusy(true);
            const command: ProducerCreateClientCommand = {
                Name: form.name,
                InitialAdminFirstName: form.firstName,
                InitialAdminLastName: form.lastName,
                InitialAdminEmail: form.email,
            };
            await new ProducerPortalClient().CreateClientCompany(accessInfo.selectedProducerCompanyId, command);
            setForm({ name: '', firstName: '', lastName: '', email: '' });
            await load();
        } catch (error) {
            setError(error, 'Failed to add client company');
        } finally {
            setBusy(false);
        }
    };

    const stateColor = (state: string): 'success' | 'warning' | 'error' | 'default' => {
        if (state === 'Active')
            return 'success';
        if (state === 'Handoff')
            return 'warning';
        return 'default';
    };

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment titleContent={<PageTitle title="Client companies" />}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Onboard and monitor your client organizations. You can still manage client billing status and access only through the producer role assignments.
                    </Typography>

                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Onboard new client company</Typography>
                            <Stack spacing={1.5}>
                                <TextField label="Client company" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size={isDesktop ? 'small' : 'medium'} fullWidth />
                                <Stack direction={isDesktop ? 'row' : 'column'} spacing={1.5}>
                                    <TextField
                                        label="Client admin first name"
                                        value={form.firstName}
                                        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                                        size={isDesktop ? 'small' : 'medium'}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Client admin last name"
                                        value={form.lastName}
                                        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                                        size={isDesktop ? 'small' : 'medium'}
                                        fullWidth
                                    />
                                </Stack>
                                <TextField
                                    label="Client admin email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    size={isDesktop ? 'small' : 'medium'}
                                    fullWidth
                                />
                                <Button
                                    disabled={!canManageClients}
                                    startIcon={<Add />}
                                    variant="contained"
                                    sx={{ minHeight: 44, alignSelf: isDesktop ? 'flex-start' : 'stretch' }}
                                    onClick={createClient}
                                >
                                    Invite initial client admin
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack spacing={1.2}>
                        {clients.map(client => <Card key={client.Id} variant="outlined">
                            <CardContent>
                                <Stack direction={isDesktop ? 'row' : 'column'} alignItems={isDesktop ? 'center' : undefined} spacing={1.25} justifyContent="space-between">
                                    <Box>
                                        <Typography fontWeight={700}>{client.Name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Billed employees: {client.ActiveBilledEmployees} · Active employees: {client.ActiveEmployees}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip size="small" color={stateColor(client.State)} label={client.State} />
                                        <Chip size="small" label={client.AssignedUsers ? `${client.AssignedUsers} team members` : 'No team members'} />
                                        <Chip size="small" label={client.ActivationState} />
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>)}
                        {clients.length === 0 && <Typography color="text.secondary">No client companies are currently assigned to this producer.</Typography>}
                    </Stack>

                    {isAdmin && (
                        <Stack sx={{ mt: 2.5 }} spacing={1}>
                            <Typography variant="subtitle1">Invite status and duplicates</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Duplicate, expired, and revoked invites are surfaced in reports and can be corrected from the team management screen.
                            </Typography>
                        </Stack>
                    )}

                </Box>
            </PanelSegment>
        </BusySpinner>
    </Box>
};

export default ProducerClientCompaniesPage;
